import { useMemo } from 'react';

import { formatCurrency } from '@/lib/utils';
import { SensitiveData } from '@/components/sensitive-data';

// Minimal shape required — compatible with both demo items and API BillingWipItem
export interface WipItemForAging {
  id: string;
  date: string;   // ISO date string e.g. "2024-01-15"
  amount: number;
}

export type AgeBand = '0-30' | '31-60' | '61-90' | '91-120' | '121+';

interface AgeBandConfig {
  id: AgeBand;
  label: string;
  minDays: number;
  maxDays: number | null;
  borderClass: string;
  activeBg: string;
  activeText: string;
  activeBorder: string;
}

const AGE_BANDS: AgeBandConfig[] = [
  {
    id: '0-30',
    label: '0-30 Days',
    minDays: 0,
    maxDays: 30,
    borderClass: 'border-l-success-500',
    activeBg: 'bg-success-50',
    activeText: 'text-success-700',
    activeBorder: 'border-success-300',
  },
  {
    id: '31-60',
    label: '31-60 Days',
    minDays: 31,
    maxDays: 60,
    borderClass: 'border-l-warning-400',
    activeBg: 'bg-warning-50',
    activeText: 'text-warning-700',
    activeBorder: 'border-warning-300',
  },
  {
    id: '61-90',
    label: '61-90 Days',
    minDays: 61,
    maxDays: 90,
    borderClass: 'border-l-warning-600',
    activeBg: 'bg-warning-100',
    activeText: 'text-warning-800',
    activeBorder: 'border-warning-400',
  },
  {
    id: '91-120',
    label: '91-120 Days',
    minDays: 91,
    maxDays: 120,
    borderClass: 'border-l-error-400',
    activeBg: 'bg-error-50',
    activeText: 'text-error-600',
    activeBorder: 'border-error-300',
  },
  {
    id: '121+',
    label: '121+ Days',
    minDays: 121,
    maxDays: null,
    borderClass: 'border-l-destructive',
    activeBg: 'bg-destructive/5',
    activeText: 'text-destructive',
    activeBorder: 'border-destructive/40',
  },
];

function getAgeDays(dateStr: string): number {
  const itemDate = new Date(dateStr);
  const today = new Date();
  const diffMs = today.getTime() - itemDate.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function getBandForDays(days: number): AgeBand {
  if (days <= 30) return '0-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  if (days <= 120) return '91-120';
  return '121+';
}

interface AgedWipStripProps {
  wipItems: WipItemForAging[];
  writtenOffIds: Set<string>;
  activeBand: AgeBand | null;
  onBandClick: (band: AgeBand | null) => void;
}

export function AgedWipStrip({
  wipItems,
  writtenOffIds,
  activeBand,
  onBandClick,
}: AgedWipStripProps) {
  const bandTotals = useMemo(() => {
    const totals: Record<AgeBand, number> = {
      '0-30': 0,
      '31-60': 0,
      '61-90': 0,
      '91-120': 0,
      '121+': 0,
    };

    wipItems.forEach(item => {
      if (writtenOffIds.has(String(item.id))) return;
      const days = getAgeDays(item.date);
      const band = getBandForDays(days);
      totals[band] += item.amount;
    });

    return totals;
  }, [wipItems, writtenOffIds]);

  const handleClick = (band: AgeBand) => {
    onBandClick(activeBand === band ? null : band);
  };

  return (
    <div className="flex items-stretch gap-3 mb-4">
        {AGE_BANDS.map(band => {
          const isActive = activeBand === band.id;
          const total = bandTotals[band.id];
          const isEmpty = total === 0;

          return (
            <button
              key={band.id}
              onClick={() => handleClick(band.id)}
              disabled={isEmpty}
              className={`
                flex-1 text-left rounded-lg border border-l-4 p-3 transition-all
                ${band.borderClass}
                ${isActive
                  ? `${band.activeBg} ${band.activeBorder} shadow-sm ring-1 ring-inset ring-current/10`
                  : 'bg-background border-border hover:bg-muted/40'
                }
                ${isEmpty ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <div className="flex items-center gap-1 mb-1.5">
                <span className={`text-xs font-medium ${isActive ? band.activeText : 'text-muted-foreground'}`}>
                  {band.label}
                </span>
              </div>
              <SensitiveData>
                <div className={`text-base font-bold tabular-nums ${isActive ? band.activeText : 'text-foreground'}`}>
                  {isEmpty ? '—' : formatCurrency(total)}
                </div>
              </SensitiveData>
            </button>
          );
        })}
    </div>
  );
}

// ─── Standalone helper for use in wip-reporting.tsx ─────────────────────────
export { getAgeDays, getBandForDays, AGE_BANDS };
